import { Globe, Check, X, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";

const SEO_CHECKS = [
  { key: "has_title", label: "Meta Title" },
  { key: "has_description", label: "Meta Description" },
  { key: "has_h1", label: "H1 Tag" },
  { key: "has_schema", label: "LocalBusiness Schema" },
  { key: "has_nap", label: "NAP on Website" },
  { key: "is_https", label: "SSL Certificate" },
  { key: "is_mobile_friendly", label: "Mobile Friendly" },
  { key: "has_alt_tags", label: "Image Alt Tags" },
  { key: "has_canonical", label: "Canonical Tag" },
  { key: "has_og_tags", label: "Open Graph Tags" },
];

function StatusIcon({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-emerald-500" />;
  if (value === false) return <X className="w-4 h-4 text-red-500" />;
  return <AlertTriangle className="w-4 h-4 text-amber-500" />;
}

function StatusLabel({ value }) {
  if (value === true) return <span className="text-xs font-medium text-emerald-600">Optimized</span>;
  if (value === false) return <span className="text-xs font-medium text-red-600">Missing</span>;
  return <span className="text-xs font-medium text-amber-600">Unknown</span>;
}

const SCHEMA_TEMPLATE = `<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "YOUR BUSINESS NAME",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "YOUR STREET",
    "addressLocality": "YOUR CITY",
    "addressRegion": "YOUR STATE",
    "postalCode": "YOUR ZIP"
  },
  "telephone": "YOUR PHONE",
  "url": "YOUR WEBSITE"
}
</script>`;

/**
 * Normalize legacy website_analysis data to boolean format.
 * Legacy format: { title: "...", meta_description: "...", h1_tags: [...], has_https, mobile_friendly: 84, ... }
 * New format:    { has_title: true, has_description: true, has_h1: true, is_https: true, is_mobile_friendly: true, ... }
 */
function normalizeWebsiteAnalysis(wa) {
  if (!wa) return wa;
  // If new-format booleans already exist, return as-is
  if (typeof wa.has_title === "boolean") return wa;
  // Map legacy fields to new boolean keys
  return {
    ...wa,
    has_title: wa.has_title ?? (wa.title ? true : false),
    has_description: wa.has_description ?? (wa.meta_description ? true : false),
    has_h1: wa.has_h1 ?? (Array.isArray(wa.h1_tags) ? wa.h1_tags.length > 0 : false),
    has_schema: wa.has_schema ?? false,
    has_nap: wa.has_nap ?? (typeof wa.nap_score === "number" ? wa.nap_score >= 50 : !!(wa.phone_on_page || wa.address_on_page)),
    is_https: wa.is_https ?? wa.has_https ?? null,
    is_mobile_friendly: wa.is_mobile_friendly ?? (typeof wa.mobile_friendly === "number" ? wa.mobile_friendly >= 50 : null),
    has_alt_tags: wa.has_alt_tags ?? null,
    has_canonical: wa.has_canonical ?? null,
    has_og_tags: wa.has_og_tags ?? null,
  };
}

export default function WebsiteSEOSection({ website_analysis }) {
  if (!website_analysis) {
    return (
      <section id="website-seo" className="mb-6" data-testid="website-seo-section">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-5 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center" style={{ background: "#6366f115" }}>
                <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-[#6366f1]" />
              </div>
              <div>
                <h3 className="font-bold text-sm sm:text-base text-[#0f172a]">Website SEO Analysis</h3>
                <p className="text-[10px] sm:text-xs text-slate-400">On-page optimization checks</p>
              </div>
            </div>
          </div>
          <div className="px-4 sm:px-6 py-8 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Website SEO data is being processed. This section will populate once the analysis completes.</p>
          </div>
        </div>
      </section>
    );
  }

  if (website_analysis.error) return null;

  const normalized = normalizeWebsiteAnalysis(website_analysis);
  // If all values are null/undefined after normalization, don't render
  const hasAnyData = SEO_CHECKS.some(c => normalized[c.key] != null);
  if (!hasAnyData) return null;

  const checks = SEO_CHECKS.map(c => ({
    ...c,
    value: normalized[c.key] ?? null,
  }));

  const passing = checks.filter(c => c.value === true).length;
  const total = checks.length;
  const score = Math.round((passing / total) * 100);
  const firstFailing = checks.find(c => c.value === false);

  const scoreColor = score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444";
  const scoreLabel = score >= 70 ? "GOOD" : score >= 40 ? "FAIR" : "POOR";

  return (
    <section id="website-seo" className="mb-6" data-testid="website-seo-section">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center" style={{ background: "#6366f115" }}>
              <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-[#6366f1]" />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base text-[#0f172a]">Website SEO Analysis</h3>
              <p className="text-[10px] sm:text-xs text-slate-400">On-page optimization checks</p>
            </div>
          </div>
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">LIVE DATA</span>
        </div>

        <div className="px-4 sm:px-6 py-5">
          <div className="space-y-2.5">
            {checks.map((check, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`seo-check-${check.key}`}>
                <div className="flex items-center gap-3">
                  <StatusIcon value={check.value} />
                  <span className="text-sm text-[#334155]">{check.label}</span>
                </div>
                <StatusLabel value={check.value} />
              </div>
            ))}
          </div>

          {/* Score bar */}
          <div className="mt-6 pt-5 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#0f172a]">Website SEO Score</span>
              <span className="text-sm font-bold" style={{ color: scoreColor }}>{score}/100</span>
            </div>
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, background: scoreColor }} />
            </div>
            <p className="text-xs font-bold mt-1.5" style={{ color: scoreColor }}>{scoreLabel}</p>
          </div>

          {/* Top fix */}
          {firstFailing && (
            <div className="mt-5 p-4 rounded-xl bg-[#fef3c7] border border-amber-200">
              <p className="text-xs font-bold text-amber-700 mb-1">Top Fix:</p>
              <p className="text-sm text-amber-800">
                {firstFailing.key === "has_schema"
                  ? "Add LocalBusiness schema markup to help Google understand your business information"
                  : firstFailing.key === "has_description"
                  ? "Add a meta description to improve click-through rates from search results"
                  : firstFailing.key === "has_title"
                  ? "Add a descriptive meta title including your business name and location"
                  : firstFailing.key === "has_nap"
                  ? "Add your Name, Address, and Phone number consistently on your website"
                  : firstFailing.key === "has_alt_tags"
                  ? "Add descriptive alt text to all images for accessibility and SEO"
                  : `Fix: ${firstFailing.label} — this improves your local search visibility`}
              </p>
              {firstFailing.key === "has_schema" && (
                <button
                  onClick={() => { navigator.clipboard.writeText(SCHEMA_TEMPLATE); toast.success("Schema template copied!"); }}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 hover:text-amber-900"
                  data-testid="copy-schema-btn"
                >
                  <Copy className="w-3 h-3" /> Copy Schema Template
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
